"use client";

import { Building2, Check, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function OrganizationSwitcher() {
  const t = useTranslations("navbar");
  const {
    activeOrganization,
    organizations,
    isSwitchingOrganization,
    switchOrganization,
  } = useAuth();

  if (!activeOrganization || organizations.length < 2) {
    return null;
  }

  const handleOrganizationChange = async (ssoOrganizationId: string) => {
    if (ssoOrganizationId === activeOrganization.sso_id || isSwitchingOrganization) {
      return;
    }

    try {
      await switchOrganization(ssoOrganizationId);
      toast.success(t("organization_switch_success"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("organization_switch_error"));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex min-w-0 max-w-[220px] items-center gap-2 rounded-md px-2 py-1.5 text-left text-white outline-none hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/70"
        aria-label={t("switch_organization")}
        disabled={isSwitchingOrganization}
      >
        {isSwitchingOrganization ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        ) : (
          <Building2 className="h-4 w-4 shrink-0" />
        )}
        <span className="truncate text-sm font-medium">{activeOrganization.name}</span>
        <ChevronDown className="h-4 w-4 shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {organizations.map((organization) => (
          <DropdownMenuItem
            key={organization.sso_id}
            disabled={isSwitchingOrganization || organization.membership_status !== "active"}
            className="flex cursor-pointer items-center justify-between gap-3"
            onClick={() => void handleOrganizationChange(organization.sso_id)}
          >
            <span className="min-w-0 truncate">{organization.name}</span>
            {organization.sso_id === activeOrganization.sso_id && (
              <Check className="h-4 w-4 shrink-0 text-[#CD2834]" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
